import React, { useState } from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import './NewFeaturesModal.css';

function NewFeaturesModal({ user, onClose }) {
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  // New features to showcase
  const features = [
    {
      icon: '📹',
      title: 'JookCam Video Calls',
      description: 'Connect with your Road Trip participants face-to-face!',
      details: [
        '🎥 Real-time video and audio communication',
        '👂 Listen-only mode for privacy',
        '🔇 Host controls: Mute all or turn off all cameras',
        '📱 Works on all devices - desktop, tablet, and mobile',
        '✨ Seamlessly integrated into Road Trip sessions'
      ],
      howTo: [
        '1. Start or join a Road Trip session',
        '2. Click the "📹 Video Call" button in the header',
        '3. Allow camera/microphone permissions when prompted',
        '4. Use the controls to mute, turn off video, or switch to listen-only mode',
        '5. Hosts can use "Mute All" and "Cameras Off" buttons to manage the session'
      ]
    },
    {
      icon: '💬',
      title: 'Road Trip Chat',
      description: 'Stay connected with instant messaging during your Road Trip!',
      details: [
        '💬 Real-time chat with all participants',
        '👤 See who sent each message',
        '⏰ Timestamped messages',
        '📱 Always accessible during Road Trip sessions'
      ],
      howTo: [
        '1. In any Road Trip session, click the "💬 Chat" button',
        '2. Type your message and press Enter',
        '3. See messages from all participants in real-time',
        '4. Chat stays open while you browse music and manage the queue'
      ]
    },
    {
      icon: '🎵',
      title: 'Smart Queue Management',
      description: 'Enhanced control over your Road Trip playlist!',
      details: [
        '✅ Songs auto-checked when added to queue',
        '🚫 Host can uncheck inappropriate songs',
        '⏸️ Disabled songs stay in queue but won\'t play',
        '🔄 Host can re-enable songs anytime'
      ],
      howTo: [
        '1. Add songs to your Road Trip queue as usual',
        '2. Songs are automatically checked (enabled) by default',
        '3. As host, click the checkbox to disable inappropriate songs',
        '4. Disabled songs remain visible but won\'t play',
        '5. Re-check to enable them again'
      ]
    }
  ];

  const currentFeature = features[currentFeatureIndex];
  const isLastFeature = currentFeatureIndex === features.length - 1;

  // Mark announcement as seen when user dismisses
  const handleDismiss = async () => {
    if (!user) {
      setDismissed(true);
      onClose();
      return;
    }

    try {
      // Mark this announcement version as seen
      const announcementId = 'new-features-dec-2024'; // Update this when creating new announcements
      const userAnnouncementsRef = doc(db, 'userAnnouncements', user.uid);
      const userAnnouncementsDoc = await getDoc(userAnnouncementsRef);

      if (userAnnouncementsDoc.exists()) {
        await updateDoc(userAnnouncementsRef, {
          [announcementId]: true,
          lastSeen: new Date().toISOString()
        });
      } else {
        await setDoc(userAnnouncementsRef, {
          [announcementId]: true,
          lastSeen: new Date().toISOString()
        });
      }

      setDismissed(true);
      onClose();
    } catch (error) {
      console.error('Error marking announcement as seen:', error);
      // Still close the modal even if save fails
      setDismissed(true);
      onClose();
    }
  };

  const handleNext = () => {
    if (isLastFeature) {
      handleDismiss();
    } else {
      setCurrentFeatureIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentFeatureIndex > 0) {
      setCurrentFeatureIndex(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    handleDismiss();
  };

  if (dismissed) return null;

  return (
    <div className="new-features-overlay" onClick={handleDismiss}>
      <div className="new-features-modal" onClick={(e) => e.stopPropagation()}>
        <div className="new-features-header">
          <div className="new-features-badge">✨ NEW</div>
          <button className="new-features-close" onClick={handleDismiss}>×</button>
        </div>

        <div className="new-features-content">
          <div className="feature-icon">{currentFeature.icon}</div>
          <h2 className="feature-title">{currentFeature.title}</h2>
          <p className="feature-description">{currentFeature.description}</p>

          <div className="feature-section">
            <h3>What's New:</h3>
            <ul className="feature-details">
              {currentFeature.details.map((detail, idx) => (
                <li key={idx}>{detail}</li>
              ))}
            </ul>
          </div>

          <div className="feature-section">
            <h3>How to Use:</h3>
            <ol className="feature-howto">
              {currentFeature.howTo.map((step, idx) => (
                <li key={idx}>{step}</li>
              ))}
            </ol>
          </div>
        </div>

        <div className="new-features-footer">
          <div className="feature-indicators">
            {features.map((_, idx) => (
              <button
                key={idx}
                className={`indicator ${idx === currentFeatureIndex ? 'active' : ''}`}
                onClick={() => setCurrentFeatureIndex(idx)}
                aria-label={`Go to feature ${idx + 1}`}
              />
            ))}
          </div>

          <div className="feature-actions">
            <button className="btn-skip" onClick={handleSkip}>
              Skip
            </button>
            {currentFeatureIndex > 0 && (
              <button className="btn-previous" onClick={handlePrevious}>
                ← Previous
              </button>
            )}
            <button className="btn-next" onClick={handleNext}>
              {isLastFeature ? 'Got it!' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewFeaturesModal;

